import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Stack,
  Grid,
  CircularProgress,
  Alert,
} from '@mui/material';
import { ConfirmDialog, SaveIndicator } from '@personal-dashboard/ui';

import TutorMemoryCard from './TutorMemoryCard';
import StartLessonCard from './StartLessonCard';
import LessonHistoryList from './LessonHistoryList';
import ActiveChatWorkspace from './ActiveChatWorkspace';
import PastLessonViewer from './PastLessonViewer';
import { useSvenTutor } from '../hooks/useSvenTutor';

export const SvenTutorPage: React.FC = () => {
  const {
    loading,
    error,
    activeLesson,
    pastLessons,
    tutorProgress,
    selectedPastLesson,
    inputText,
    setInputText,
    selectedPastLessonId,
    setSelectedPastLessonId,
    showEndLessonDialog,
    setShowEndLessonDialog,
    isSending,
    startStatus,
    endStatus,
    messagesEndRef,
    handleStartLesson,
    handleSendMessage,
    handleEndLesson,
    handleConfirmEndLesson,
  } = useSvenTutor();

  if (loading && !activeLesson) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ my: 2 }}>
        Failed to connect with Sven: {error.message}
      </Alert>
    );
  }

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={4} sx={{ display: (activeLesson || selectedPastLesson) ? { xs: 'none', md: 'block' } : 'block' }}>
        <Stack spacing={3}>
          <TutorMemoryCard tutorProgress={tutorProgress} />

          {!activeLesson && (
            <>
              <StartLessonCard onStartLesson={handleStartLesson} />
              <SaveIndicator status={startStatus} />
            </>
          )}

          <LessonHistoryList
            pastLessons={pastLessons}
            selectedPastLessonId={selectedPastLessonId}
            onSelectLesson={setSelectedPastLessonId}
            hasActiveLesson={!!activeLesson}
          />
        </Stack>
      </Grid>

      <Grid item xs={12} md={8} sx={{ display: !(activeLesson || selectedPastLesson) ? { xs: 'none', md: 'block' } : 'block' }}>
        {activeLesson ? (
          <ActiveChatWorkspace
            activeLesson={activeLesson}
            inputText={inputText}
            onInputChange={setInputText}
            isSending={isSending}
            onSendMessage={handleSendMessage}
            onEndLesson={handleEndLesson}
            messagesEndRef={messagesEndRef}
          />
        ) : selectedPastLesson ? (
          <PastLessonViewer
            selectedPastLesson={selectedPastLesson}
            onClose={() => setSelectedPastLessonId(null)}
          />
        ) : (
          <Paper
            elevation={0}
            sx={{
              p: 4,
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              height: 550,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              textAlign: 'center',
              backgroundColor: 'action.hover',
            }}
          >
            <Typography variant="h4" sx={{ fontWeight: 'black', mb: 2 }}>
              Hej student! 🇸🇪
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 450, mb: 3 }}>
              Select a lesson from your history to review or type a title on the left to start a brand new Swedish dialogue lesson with Sven!
            </Typography>
            <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: '1px solid', borderColor: 'divider', textAlign: 'left', maxWidth: 450 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                💡 Sven can help with:
              </Typography>
              <Typography variant="body2" color="text.secondary" component="div">
                <ul>
                  <li>Contextual explanations of any Swedish word or set</li>
                  <li>Real Swedish conversational practice and dialogue games</li>
                  <li>In-depth explanations of complex grammar (like &ldquo;En&rdquo; vs &ldquo;Ett&rdquo;)</li>
                </ul>
              </Typography>
            </Paper>
          </Paper>
        )}
      </Grid>

      <ConfirmDialog
        open={showEndLessonDialog}
        title="Complete Practice Session"
        message="Save a lesson summary and end this session?"
        confirmLabel="Complete Session"
        severity="info"
        onConfirm={handleConfirmEndLesson}
        onCancel={() => setShowEndLessonDialog(false)}
      />
      <SaveIndicator status={endStatus} />
    </Grid>
  );
};
